import { ref, watch, computed } from 'vue';
import { updateAdminDetails, updateAdminType } from '@/api/admin';
import Alerts from '@/lib/alerts';

export function useAdminInlineEdit(
    currentUserRef,
    refreshAdminsFn,
    onClickEditPasswordFn,
    onClickEditTagsFn,
    isMfaEnabledRef
) {
    const editingAdmin = ref(false);
    const editingAdminId = ref(null);
    const editingAdminType = ref(null);
    const currentPermission = ref('');
    const currentKbAccess = ref(false);
    const currentStudentUploadAccess = ref(false);
    const currentFirstName = ref('');
    const currentLastName = ref('');
    const currentEmail = ref('');
    const currentPhoneNumber = ref('');

    // --- Computed properties for permissions ---
    const isCurrentUserStaff = computed(() => currentUserRef.value?.isStaff);
    const isCurrentUserUnlimited = computed(() => {
        const userType = currentUserRef.value?.adminAccount?.type;
        return userType === 'MASTER' || userType === 'UNLIMITED';
    });

    // Controls if Name/Email fields are editable.
    const canEditNameEmail = computed(() => {
        // Staff can always edit
        if (isCurrentUserStaff.value) {
            return true;
        }
        return false;
    });

    // Controls if Phone Number field is editable.
    const canEditPhoneNumber = computed(() => {
        // Staff can always edit
        if (isCurrentUserStaff.value) {
            return true;
        }
        // User can always edit their own phone number
        if (editingAdminId.value === currentUserRef.value?.id) {
            return true;
        }
        // Otherwise, editing is not allowed.
        return false;
    });

    // Controls if the Permission dropdown is editable.
    const canEditPermission = computed(() => {
        // Staff can always edit
        if (isCurrentUserStaff.value) {
            return true;
        }
        // Unlimited admins can edit other admins' permissions
        // Unlimited admins cannot demote themselves to LIMITED
        if (isCurrentUserUnlimited.value && editingAdminId.value !== currentUserRef.value?.id) {
            return true;
        }
        // Otherwise, editing is not allowed.
        return false;
    });

    // --- Edit State Management ---
    const onClickEditAdmin = (admin) => {
        editingAdmin.value = true;
        editingAdminId.value = admin.id;
        editingAdminType.value = admin.adminAccount?.type;
        currentPermission.value =
            admin.adminAccount.type === 'MASTER' ? 'UNLIMITED' : admin.adminAccount.type;
        currentKbAccess.value =
            currentPermission.value === 'UNLIMITED' ? true : admin.adminAccount.canAccessKb;
        currentStudentUploadAccess.value =
            currentPermission.value === 'UNLIMITED' ? true : admin.adminAccount.canAccessStudentUploads;
        currentFirstName.value = admin.firstName || '';
        currentLastName.value = admin.lastName || '';
        currentEmail.value = admin.email;

        const rawPhoneNumber = admin.phoneNumber || '';
        if (rawPhoneNumber.startsWith('+1') && rawPhoneNumber.length === 12) {
            currentPhoneNumber.value = rawPhoneNumber.substring(2);
        } else {
            currentPhoneNumber.value = rawPhoneNumber.replace(/\D/g, '');
        }
    };

    const cancelAdminChanges = () => {
        editingAdmin.value = false;
        editingAdminId.value = null;
        editingAdminType.value = null;
        currentPermission.value = '';
        currentKbAccess.value = false;
        currentStudentUploadAccess.value = false;
        currentFirstName.value = '';
        currentLastName.value = '';
        currentEmail.value = '';
        currentPhoneNumber.value = '';
    };

    // --- Save Logic ---
    const saveAdminChanges = async (admin) => {
        const originalAdminData = JSON.parse(JSON.stringify(admin));

        let detailsUpdatePromise = Promise.resolve();
        const detailsPayload = { id: admin.id };
        let detailsChanged = false;

        // --- Validate and Prepare Details Payload ---
        if (canEditNameEmail.value) {
            const newFirstName = currentFirstName.value.trim();
            const newLastName = currentLastName.value.trim();
            if (
                newFirstName !== originalAdminData.firstName ||
                newLastName !== originalAdminData.lastName
            ) {
                if (!newFirstName || !newLastName) {
                    Alerts.alert({
                        type: 'error',
                        message: 'First and Last name cannot be empty.',
                        timeout: 5000
                    });
                    return;
                }
                detailsPayload.firstName = newFirstName;
                detailsPayload.lastName = newLastName;
                detailsChanged = true;
            }

            const newEmail = currentEmail.value.trim();
            if (newEmail !== originalAdminData.email) {
                if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) {
                    Alerts.alert({
                        type: 'error',
                        message: 'Please enter a valid email address.',
                        timeout: 5000
                    });
                    return;
                }
                detailsPayload.email = newEmail;
                detailsChanged = true;
            }
        }

        if (canEditPhoneNumber.value) {
            const newPhoneNumberInput = currentPhoneNumber.value || '';
            const originalPhoneNumberE164 = originalAdminData.phoneNumber || '';
            const newPhoneNumberCleaned = newPhoneNumberInput.replace(/\D/g, '');
            let formattedPhoneNumber = null;
            let phoneValidationFailed = false;

            if (newPhoneNumberCleaned) {
                if (newPhoneNumberCleaned.length === 10) {
                    formattedPhoneNumber = `+1${newPhoneNumberCleaned}`;
                } else {
                    Alerts.alert({
                        type: 'error',
                        message: 'Phone number must be 10 digits (e.g., 5551234567).',
                        timeout: 5000
                    });
                    phoneValidationFailed = true;
                }
            // Block numbers from being removed if MFA is enabled
            // Also allow for edge case: MFA enabled, but no phone number for a user
            // allow user to still be editable for permissions.
            } else if (isMfaEnabledRef.value && originalPhoneNumberE164 && !newPhoneNumberCleaned) {
                Alerts.alert({
                    type: 'error',
                    message: 'Cannot remove phone number while MFA is enabled.',
                    timeout: 5000
                });
                phoneValidationFailed = true;
            }

            if (phoneValidationFailed) {
                return;
            }

            if (formattedPhoneNumber !== originalPhoneNumberE164) {
                detailsPayload.phoneNumber = formattedPhoneNumber;
                detailsChanged = true;
            }
        }

        if (detailsChanged) {
            detailsUpdatePromise = updateAdminDetails(detailsPayload);
        }

        // --- Validate Permission Type/KB Access Payload ---
        let typeUpdatePromise = Promise.resolve();
        const newType = currentPermission.value === 'UNLIMITED' ? 'MASTER' : currentPermission.value;
        const newKbAccess = currentPermission.value === 'UNLIMITED' ? true : currentKbAccess.value;
        const newStudentUploadAccess = currentPermission.value === 'UNLIMITED' ? true : currentStudentUploadAccess.value;
        let typeChanged = false;

        if (
            newType !== originalAdminData.adminAccount.type ||
            newKbAccess !== originalAdminData.adminAccount.canAccessKb ||
            newStudentUploadAccess !== originalAdminData.adminAccount.canAccessStudentUploads
        ) {
            const typePayload = {
                id: admin.id,
                type: newType,
                canAccessKb: newKbAccess,
                canAccessStudentUploads: newStudentUploadAccess
            };

            typeUpdatePromise = updateAdminType({ ...typePayload });

            typeChanged = true;
        }

        // --- Execute API Calls ---
        try {
            if (!detailsChanged && !typeChanged) {
                cancelAdminChanges();
                return;
            }

            await Promise.all([detailsUpdatePromise, typeUpdatePromise]);
            Alerts.alert({
                type: 'success',
                message: 'Admin details updated successfully.',
                timeout: 5000
            });
            cancelAdminChanges();
            await refreshAdminsFn();
        } catch (error) {
            console.error('Failed to update admin:', error);
            Alerts.alert({
                type: 'error',
                message: 'Failed to update admin details. Please try again.',
                timeout: 5000
            });
            await refreshAdminsFn();
            cancelAdminChanges();
        }
    };

    // --- Watcher ---
    watch(currentPermission, (newPermission) => {
        if (newPermission === 'UNLIMITED') {
            currentKbAccess.value = true;
            currentStudentUploadAccess.value = true;
        }
    });

    // --- Utility Functions ---
    const adminDisplayValue = (type) => {
        return type === 'MASTER' ? 'UNLIMITED' : type;
    };

    const getAdminMenuItems = (adminData) => {
        const items = [];
        const targetUserId = adminData.id;
        const targetUserType = adminData.adminAccount?.type;
        const isTargetLimited = targetUserType === 'LIMITED';

        // --- Edit User Logic ---
        let canEditThisUser = false;
        if (isCurrentUserStaff.value || isCurrentUserUnlimited.value) {
            canEditThisUser = true;
        }

        if ((!editingAdmin.value || editingAdminId.value !== targetUserId) && canEditThisUser) {
            items.push({
                label: 'Edit User',
                icon: 'pencil',
                command: () => onClickEditAdmin(adminData)
            });
        }

        // --- Edit Tags Logic ---
        if (isTargetLimited) {
            items.push({
                label: 'Edit Tags',
                icon: 'taggroup',
                command: () => onClickEditTagsFn(adminData)
            });
        }

        // --- Reset Password Logic ---
        const canResetPassword = isCurrentUserStaff.value;
        if (canResetPassword) {
            items.push({
                label: 'Reset Password',
                icon: 'key',
                command: () => onClickEditPasswordFn(adminData)
            });
        }

        return items;
    };

    return {
        editingAdmin,
        editingAdminId,
        currentPermission,
        currentKbAccess,
        currentStudentUploadAccess,
        currentFirstName,
        currentLastName,
        currentEmail,
        currentPhoneNumber,
        canEditNameEmail,
        canEditPhoneNumber,
        canEditPermission,
        isCurrentUserUnlimited,
        saveAdminChanges,
        cancelAdminChanges,
        adminDisplayValue,
        getAdminMenuItems
    };
}
